/**
 * Advisor 結果表示カードコンポーネント（管理アプリ用）
 *
 * Advisor ツールの実行結果（アドバイス内容・使用量）を表示する。
 */

import { isAdvisorResult } from '@kyumo/common';
import { BrainCircuit, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { formatCost, formatNumber } from './chart-utils';
import { MarkdownContent } from './MarkdownContent';

interface AdvisorUsage {
  inputTokens: number;
  outputTokens: number;
  reasoningTokens?: number;
  cacheReadTokens?: number;
  cacheWriteTokens?: number;
  cost?: number;
  costs?: {
    total: number;
    input?: number;
    output?: number;
    reasoning?: number;
    cacheRead?: number;
    cacheWrite?: number;
  };
}

/** 管理アプリ用の拡張 Advisor 結果型（詳細なトークン情報を含む） */
interface ExtendedAdvisorSuccessResult {
  status: 'success';
  advice: string;
  usage?: AdvisorUsage;
  advisorModelName?: string;
}

interface ExtendedAdvisorErrorResult {
  status: 'error' | 'limit_reached';
  message: string;
}

type ExtendedAdvisorResult = ExtendedAdvisorSuccessResult | ExtendedAdvisorErrorResult;

function totalTokens(usage: AdvisorUsage): number {
  return (
    usage.inputTokens +
    usage.outputTokens +
    (usage.reasoningTokens ?? 0) +
    (usage.cacheReadTokens ?? 0) +
    (usage.cacheWriteTokens ?? 0)
  );
}

export interface AdvisorResultCardProps {
  result: unknown;
}

/**
 * Advisor 結果カード
 */
export function AdvisorResultCard({ result }: AdvisorResultCardProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!isAdvisorResult(result)) return null;

  // isAdvisorResult で基本構造を検証済み。管理アプリでは詳細なトークン情報も扱う
  const advisorResult = result as ExtendedAdvisorResult;
  const isSuccess = advisorResult.status === 'success';
  const isError = advisorResult.status === 'error';
  const isLimitReached = advisorResult.status === 'limit_reached';

  const costTotal = isSuccess
    ? (advisorResult.usage?.costs?.total ?? advisorResult.usage?.cost)
    : undefined;

  return (
    <div className="mt-2 rounded-lg border border-violet-500/30 overflow-hidden">
      {/* ヘッダー */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm cursor-pointer hover:bg-muted/30 ${
          isSuccess ? 'bg-violet-500/10' : isError ? 'bg-red-500/10' : 'bg-amber-500/10'
        }`}
      >
        <ChevronRight
          className={`h-4 w-4 transition-transform text-muted-foreground ${isOpen ? 'rotate-90' : ''}`}
        />
        <BrainCircuit
          className={`h-4 w-4 ${
            isSuccess ? 'text-violet-400' : isError ? 'text-red-400' : 'text-amber-400'
          }`}
        />
        <span className="font-medium text-foreground">
          {isSuccess && 'Advisor のアドバイス'}
          {isError && 'Advisor エラー'}
          {isLimitReached && 'Advisor 上限到達'}
        </span>

        {/* 使用量（成功時のみ） */}
        {isSuccess && advisorResult.usage && (
          <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <span>{formatNumber(totalTokens(advisorResult.usage))} tokens</span>
            {costTotal !== undefined && (
              <span className="text-yellow-400">{formatCost(costTotal)}</span>
            )}
          </span>
        )}
      </button>

      {/* コンテンツ */}
      {isOpen && (
        <div className="border-t border-border px-4 py-3">
          {isSuccess ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <MarkdownContent content={advisorResult.advice} />
            </div>
          ) : (
            <p className={`text-sm ${isError ? 'text-red-400' : 'text-amber-400'}`}>
              {advisorResult.message}
            </p>
          )}

          {/* 詳細使用量（成功時のみ） */}
          {isSuccess && advisorResult.usage && (
            <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border pt-2 text-xs text-muted-foreground">
              {advisorResult.advisorModelName && (
                <span className="rounded bg-muted px-1.5 py-0.5 font-mono">
                  {advisorResult.advisorModelName}
                </span>
              )}
              <span>入力: {formatNumber(advisorResult.usage.inputTokens)}</span>
              <span>出力: {formatNumber(advisorResult.usage.outputTokens)}</span>
              {(advisorResult.usage.reasoningTokens ?? 0) > 0 && (
                <span>推論: {formatNumber(advisorResult.usage.reasoningTokens!)}</span>
              )}
              {(advisorResult.usage.cacheReadTokens ?? 0) > 0 && (
                <span>キャッシュ読込: {formatNumber(advisorResult.usage.cacheReadTokens!)}</span>
              )}
              {(advisorResult.usage.cacheWriteTokens ?? 0) > 0 && (
                <span>キャッシュ書込: {formatNumber(advisorResult.usage.cacheWriteTokens!)}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
